// =====================================================================
// SecuriteEtiquettes.jsx — M-010 : impression des autocollants QR
// =====================================================================
// Le module entier repose sur un geste : l'opérateur scanne l'autocollant
// collé sur la trousse (ou l'extincteur, ou l'engin). Sans autocollants
// imprimés, rien de tout le reste ne sert. C'est donc la pièce qui
// débloque l'usage sur le terrain.
//
// Ce que le QR contient : une URL de CETTE application, pas un code nu.
//   https://zeus.brain-log.com/securite?scan=BS-001
// Conséquence pratique : l'appareil photo du téléphone suffit. Pas besoin
// d'ouvrir Zeus d'abord, pas besoin d'un lecteur dédié — on scanne et on
// tombe directement sur la fiche de l'objet. Un opérateur qui découvre un
// collègue blessé n'a pas à se souvenir d'un chemin dans un menu.
//
// Pourquoi imprimer via le navigateur plutôt qu'un PDF fabriqué côté
// serveur : zéro dépendance de plus, et la même technique servira à
// exporter le registre. Ctrl+P → « Enregistrer au format PDF » donne
// aussi le PDF, si on veut l'envoyer à l'imprimante du dépôt.
//
// L'aperçu est à l'échelle réelle (millimètres) : ce qui est à l'écran
// sort tel quel sur la planche d'étiquettes.
// =====================================================================

import { useState, useEffect, useMemo, useCallback } from 'react';
import { safetyPlans, errMsg } from '../services/api.js';

const C = {
  line: '#e5e7eb', ink: '#3f4654', mute: '#7b8494', brand: '#714B67',
  bg: '#f8fafc', crit: '#dc2626',
};

// Planches d'étiquettes autocollantes, géométrie Avery réelle en millimètres.
// Faites un essai sur papier ordinaire avant la planche : le calage dépend
// aussi de l'imprimante, et une planche mal cadrée est perdue.
const FORMATS = {
  L7160: {
    label: 'Avery L7160 — 63,5 × 38,1 mm (3 × 7 = 21 par page)',
    hint: 'extincteurs, engins, sorties de secours',
    w: 63.5, h: 38.1, cols: 3, rows: 7, gap: 2.5, mx: 7.25, my: 15.15, qr: 28,
    big: false,
  },
  L7165: {
    label: 'Avery L7165 — 99,1 × 67,7 mm (2 × 4 = 8 par page)',
    hint: 'boîtes de secours : lisible de loin, quand quelqu’un est blessé',
    w: 99.1, h: 67.7, cols: 2, rows: 4, gap: 2.5, mx: 4.65, my: 13.1, qr: 50,
    big: true,
  },
};

const KIND = {
  BOITE_SECOURS:  { label: 'Boîtes de secours', short: 'Boîte de secours',
                    action: 'Accident ? Scannez ce code', urgent: true },
  EXTINCTEUR:     { label: 'Extincteurs', short: 'Extincteur',
                    action: 'Contrôle ou anomalie : scannez' },
  DEVIDOIR:       { label: 'Dévidoirs (RIA)', short: 'Dévidoir',
                    action: 'Contrôle ou anomalie : scannez' },
  ALARME:         { label: 'Déclencheurs d’alarme', short: 'Déclencheur',
                    action: 'Anomalie : scannez' },
  MACHINE:        { label: 'Engins et machines', short: 'Engin',
                    action: 'Contrôle ou anomalie : scannez' },
  SORTIE_SECOURS: { label: 'Sorties de secours', short: 'Sortie de secours',
                    action: 'Issue bloquée ? Scannez' },
  AVERTISSEMENT:  { label: 'Panneaux d’avertissement', short: 'Panneau',
                    action: 'Anomalie : scannez' },
  RACK:           { label: 'Racks', short: 'Rack',
                    action: 'Scannez plutôt l’étiquette de la lisse' },
};

// Les racks ne sont pas proposés : chaque lisse porte déjà son étiquette
// d'emplacement, on s'appuie sur celle-là (décision du 25/08).
const OFFERED = ['BOITE_SECOURS', 'EXTINCTEUR', 'DEVIDOIR', 'ALARME',
  'MACHINE', 'SORTIE_SECOURS', 'AVERTISSEMENT'];

const card = {
  background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12,
  padding: '1rem 1.1rem', boxShadow: '0 1px 3px rgba(60,50,90,.06)',
};
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600,
  color: C.mute, textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 4 };

export default function SecuriteEtiquettes() {
  const [assets, setAssets] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  const [format, setFormat] = useState('L7160');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);
  const [qr, setQr] = useState({});          // code -> markup SVG
  const [origin, setOrigin] = useState('');

  useEffect(() => { setOrigin(window.location.origin); }, []);

  useEffect(() => {
    (async () => {
      try { setAssets(await safetyPlans.allAssets(OFFERED)); }
      catch (e) { setErr(errMsg(e)); }
      finally { setBusy(false); }
    })();
  }, []);

  // Le TYPE de l'objet decide ou mene son autocollant. Une trousse de
  // secours ouvre la declaration d'accident ; un extincteur, un rack ou
  // un engin ouvrent le signalement d'anomalie. La personne au pied de
  // l'objet ne devrait pas avoir a choisir : le geste le plus probable
  // doit s'ouvrir tout seul. Chaque ecran porte un lien vers l'autre
  // pour le cas ou elle s'est trompee.
  const urlFor = useCallback((code, kind) => {
    const base = kind === 'BOITE_SECOURS' ? '/accidents' : '/securite';
    return `${origin}${base}?scan=${encodeURIComponent(code)}`;
  }, [origin]);

  // Encodage à la demande : on ne fabrique le QR que des objets cochés.
  // L'import est paresseux — la page de déclaration n'a pas à charger
  // l'encodeur pour rien.
  useEffect(() => {
    if (!origin || !picked.size) return;
    let alive = true;
    (async () => {
      const { default: QRCode } = await import('qrcode');
      const todo = [...picked].filter((c) => !qr[c]);
      if (!todo.length) return;
      const next = {};
      for (const code of todo) {
        try {
          const objet = assets.find((a) => a.code === code);
          next[code] = await QRCode.toString(urlFor(code, objet?.kind), {
            type: 'svg', margin: 0, errorCorrectionLevel: 'M',
          });
        } catch { /* un code impossible à encoder ne bloque pas les autres */ }
      }
      if (alive && Object.keys(next).length) setQr((q) => ({ ...q, ...next }));
    })();
    return () => { alive = false; };
  }, [picked, origin, urlFor, qr, assets]);

  const byKind = useMemo(() => {
    const m = {};
    for (const a of assets) (m[a.kind] = m[a.kind] || []).push(a);
    return m;
  }, [assets]);

  const sheet = useMemo(
    () => assets.filter((a) => picked.has(a.code)), [assets, picked],
  );

  function toggle(code) {
    setPicked((p) => { const n = new Set(p); n.has(code) ? n.delete(code) : n.add(code); return n; });
  }
  function toggleKind(kind) {
    const codes = (byKind[kind] || []).map((a) => a.code);
    const allIn = codes.every((c) => picked.has(c));
    setPicked((p) => {
      const n = new Set(p);
      codes.forEach((c) => (allIn ? n.delete(c) : n.add(c)));
      return n;
    });
  }

  const F = FORMATS[format];
  const perPage = F.cols * F.rows;

  if (busy) return <p style={{ color: C.mute }}>Chargement des objets…</p>;

  return (
    <div>
      {/* --- feuille de style d'impression -------------------------------
          À l'écran : aperçu sur fond gris, avec la partie sélection.
          À l'impression : QUE les étiquettes, à l'échelle exacte. */}
      <style>{`
        @media print {
          @page { size: A4; margin: ${F.my}mm ${F.mx}mm; }
          body * { visibility: hidden !important; }
          #planche, #planche * { visibility: visible !important; }
          #planche {
            position: absolute; left: 0; top: 0; width: 100%;
            background: #fff !important; padding: 0 !important;
            column-gap: ${F.gap}mm !important; row-gap: 0 !important;
          }
          .etq { break-inside: avoid; border: none !important; }
          .noprint { display: none !important; }
        }
      `}</style>

      {err && (
        <div style={{ ...card, borderColor: '#f3c7c7', background: '#fdf0f0',
          color: C.crit, marginBottom: '0.9rem' }}>{err}</div>
      )}

      <div className="noprint" style={{ ...card, marginBottom: '0.9rem' }}>
        <h3 style={{ margin: '0 0 .35rem', fontSize: '1rem', color: C.ink }}>
          Autocollants à coller sur les objets
        </h3>
        <p style={{ margin: '0 0 .8rem', fontSize: '0.86rem', color: C.mute, maxWidth: '62ch' }}>
          Chaque code renvoie vers cette application, sur la fiche de l’objet :
          l’appareil photo du téléphone suffit, il n’y a pas de lecteur à installer.
          Imprimez sur une planche d’étiquettes autocollantes, puis collez —
          la boîte de secours au niveau des yeux, l’extincteur sur le support,
          l’engin près du poste de conduite.
        </p>

        <div style={{ display: 'flex', gap: '1.4rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ minWidth: 320 }}>
            <label style={lbl} htmlFor="fmt">Format de planche</label>
            <select id="fmt" value={format} onChange={(e) => setFormat(e.target.value)}
              style={{ width: '100%', padding: '.5rem .6rem', border: `1px solid ${C.line}`,
                borderRadius: 8, fontSize: '.92rem' }}>
              {Object.entries(FORMATS).map(([k, f]) => (
                <option key={k} value={k}>{f.label}</option>
              ))}
            </select>
            <p style={{ fontSize: '.78rem', color: C.mute, margin: '4px 0 0' }}>{F.hint}</p>
          </div>
          <div style={{ fontSize: '.88rem', color: C.ink }}>
            <b>{sheet.length}</b> étiquette(s) sélectionnée(s) ·{' '}
            {sheet.length ? Math.ceil(sheet.length / perPage) : 0} page(s) de {perPage}
          </div>
          <button type="button" disabled={!sheet.length} onClick={() => window.print()}
            style={{ padding: '.55rem 1.1rem', borderRadius: 8, border: 'none',
              background: sheet.length ? C.brand : '#cbd5e1', color: '#fff',
              fontWeight: 600, cursor: sheet.length ? 'pointer' : 'default' }}>
            Imprimer la planche
          </button>
        </div>

        {origin && (
          <p style={{ fontSize: '.78rem', color: C.mute, margin: '.75rem 0 0' }}>
            Les codes pointeront vers <code style={{ background: '#f1f5f9',
              padding: '1px 5px', borderRadius: 4 }}>{origin}</code>. Une boîte de
            secours ouvre la <b>déclaration d’accident</b> ; les autres objets
            ouvrent le <b>signalement d’anomalie</b>.
            {origin.includes('localhost') && (
              <b style={{ color: C.crit }}> Attention : sur cette adresse de test, les
                autocollants ne fonctionneront pas depuis un téléphone. Imprimez la
                planche définitive depuis l’adresse de production.</b>
            )}
          </p>
        )}
      </div>

      {/* --- sélection --------------------------------------------------- */}
      <div className="noprint" style={{ display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '.9rem',
        marginBottom: '1.2rem' }}>
        {OFFERED.filter((k) => (byKind[k] || []).length).map((k) => {
          const list = byKind[k];
          const on = list.filter((a) => picked.has(a.code)).length;
          return (
            <div key={k} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'baseline', marginBottom: '.5rem' }}>
                <b style={{ fontSize: '.94rem', color: C.ink }}>{KIND[k].label}</b>
                <button type="button" onClick={() => toggleKind(k)}
                  style={{ border: `1px solid ${C.line}`, background: '#fff',
                    borderRadius: 6, padding: '.2rem .5rem', fontSize: '.78rem',
                    cursor: 'pointer', color: C.brand }}>
                  {on === list.length ? 'tout retirer' : 'tout prendre'}
                </button>
              </div>
              <div style={{ maxHeight: 190, overflowY: 'auto' }}>
                {list.map((a) => (
                  <label key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline',
                    fontSize: '.85rem', padding: '2px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={picked.has(a.code)}
                      onChange={() => toggle(a.code)} />
                    <span>
                      {a.name || KIND[k].short}
                      {a.locationText && (
                        <span style={{ color: C.mute }}> — {a.locationText}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
              <p style={{ fontSize: '.76rem', color: C.mute, margin: '.4rem 0 0' }}>
                {on} / {list.length} sélectionné(s)
              </p>
            </div>
          );
        })}
      </div>

      {/* --- la planche, à l'échelle réelle ------------------------------ */}
      {!!sheet.length && (
        <>
          <p className="noprint" style={{ fontSize: '.82rem', color: C.mute,
            margin: '0 0 .5rem' }}>
            Aperçu à l’échelle réelle — ce qui est ci-dessous sort tel quel.
          </p>
          <div id="planche" style={{
            display: 'grid', gridTemplateColumns: `repeat(${F.cols}, ${F.w}mm)`,
            columnGap: `${F.gap}mm`, rowGap: 0,
            background: '#eef0f4', padding: '4mm', borderRadius: 8, width: 'fit-content',
          }}>
            {sheet.map((a) => {
              const k = KIND[a.kind] || KIND.MACHINE;
              return (
                <div key={a.id} className="etq" style={{
                  width: `${F.w}mm`, height: `${F.h}mm`, background: '#fff',
                  border: '1px dashed #cbd5e1', boxSizing: 'border-box',
                  padding: '2.2mm', display: 'flex', gap: '2.2mm', alignItems: 'center',
                  overflow: 'hidden',
                }}>
                  <div style={{ width: `${F.qr}mm`, height: `${F.qr}mm`, flex: '0 0 auto' }}
                    dangerouslySetInnerHTML={{ __html: qr[a.code] || '' }} />
                  <div style={{ minWidth: 0, lineHeight: 1.25 }}>
                    {/* Le pictogramme sur l'autocollant sert au moment de
                        les coller : on voit lequel va sur quoi sans lire
                        le code. */}
                    {a.picto && (
                      <img src={`/picto/${a.picto}`} alt="" style={{
                        width: F.big ? '9mm' : '5.5mm',
                        height: F.big ? '9mm' : '5.5mm',
                        objectFit: 'contain', float: 'right',
                        marginLeft: '1.5mm',
                      }} />
                    )}
                    <div style={{
                      fontSize: F.big ? '3.4mm' : '2.4mm',
                      fontWeight: 700, color: k.urgent ? C.crit : C.ink,
                      textTransform: 'uppercase', letterSpacing: '.02em',
                    }}>{k.action}</div>
                    <div style={{
                      fontSize: F.big ? '4.6mm' : '3.1mm',
                      fontWeight: 700, color: '#111827', marginTop: '1mm',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{a.name || k.short}</div>
                    {a.locationText && (
                      <div style={{ fontSize: F.big ? '3.3mm' : '2.3mm',
                        color: '#4b5563', marginTop: '.6mm' }}>{a.locationText}</div>
                    )}
                    <div style={{ fontSize: F.big ? '2.9mm' : '2.1mm',
                      color: '#6b7280', marginTop: '1.2mm', fontFamily: 'monospace' }}>
                      {a.code}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
